import { stripHtmlTags } from '@change-corgi/core/html';

import { usePetitionDetailsPageData } from 'src/app/pages/petitionGamma/details/pageContext';
import { getUsState } from 'src/app/shared/utils/unitedStatesMapping';

import type { DecisionMakers as ApiDm } from '../../../../hooks/decisionMakers';

import { getPrimaryDms } from './utils';

export type DecisionMaker = {
	id: string;
	displayName: string;
	slug: string | null;
	title: string | null;
	location: string | null;
	photoUrl: string | null;
	signersText: string;
	publiclyVisible: boolean;
	response?: string;
};

export type LegislativeBody = {
	title: string;
	location: string | null;
	signersText: string;
	displayedDMs: readonly DecisionMaker[];
	displayViewMoreButton: boolean;
};

export type AllDmResponses = Array<{
	id: string;
	createdAtLocalized: string;
	message: string;
	decisionMaker: { id: string; displayName: string };
}>;

type Result = ModelHookResult<{
	primaryDMs: DecisionMaker[] | [];
	legislativeBodies: LegislativeBody[];
	displayViewAllButton: boolean;
}>;

const DISPLAYED_DM_COUNT = 3;

function getConstituencyStat(constituentCount: string | null | undefined): string {
	if (!constituentCount) return '';
	return `Represents ${constituentCount} petition ${constituentCount === '1' ? 'signer' : 'signers'}`;
}

function normalizeDms(dms: ApiDm, allDmResponses: AllDmResponses): DecisionMaker[] {
	return dms.map((dm) => {
		const dmResponse = allDmResponses.find((response) => response.decisionMaker.id === dm.id);

		return {
			id: dm.id,
			displayName: dm.displayName,
			slug: dm.slug,
			title: dm.title,
			location: dm.state ? getUsState(dm.state) : null,
			photoUrl: dm.photo?.userMedium.url ? dm.photo.userMedium.url : null,
			signersText: getConstituencyStat(dm.targetPetitionAssociationData?.constituentCountAmongSignersLocalized),
			publiclyVisible: dm.publiclyVisible,
			...(dmResponse?.message
				? { response: `${dmResponse.createdAtLocalized}: ${stripHtmlTags(dmResponse.message)}` }
				: {}),
		};
	});
}

export function useDecisionMakers(): Result {
	const {
		data: {
			petition: {
				primaryDecisionMakerConnection: { nodes: dms },
				dmResponsesConnection: { nodes: allDmResponses },
			},
		},
	} = usePetitionDetailsPageData();

	const allDms = normalizeDms(dms, [...allDmResponses]);

	const legislativeBodies = [
		{
			title: 'City Council',
			location: 'Arizona',
			signersText: '8 members representing 2,132 signers',
			decisionMakers: [...allDms],
		},
	];

	return {
		data: {
			primaryDMs: getPrimaryDms(allDms),
			legislativeBodies: legislativeBodies.map(({ decisionMakers, ...rest }) => ({
				...rest,
				displayedDMs: decisionMakers.slice(0, DISPLAYED_DM_COUNT),
				displayViewMoreButton: decisionMakers.length > DISPLAYED_DM_COUNT,
			})),
			displayViewAllButton: true,
		},
	};
}
